import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

const state = {
  // Menu state
  activeSecMenu: {},
  primaryMenus: [],
  currentMenu: null,

  // UI state
  siteDrawer: false,
  mobileMenuOpen: false,
  subMenuOpen: {},

  // Loading states
  loadingMenus: false,
  loadingCurrentMenu: false,
  loadingScreenData: false,

  // Error state
  error: null
};

const actions = {
  /**
   * Set active secondary menu
   */
  setActiveSecMenu({ commit }, payload) {
    console.log("*********setActiveSecMenu", payload);
    commit("setActiveSecMenu", payload);
  },

  /**
   * Fetch screen/page data for menu content
   */
  async fetchScreenData({ commit, getters }, payload) {
    console.log("*********fetchScreenData", payload);
    if (!payload || !payload.contentId) {
      console.warn("fetchScreenData: No content ID provided, skipping fetch");
      commit("setActiveSecMenu", {});
      return null;
    }

    commit("setLoadingScreenData", true);
    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const pageRef = doc(collection(programRef, "pages"), payload.contentId);
      const querySnapshot = await getDoc(pageRef);

      if (querySnapshot.exists()) {
        const data = querySnapshot.data();
        const screenData = {
          id: querySnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };

        commit("setActiveSecMenu", screenData);
        return screenData;
      } else {
        console.warn(`Screen data with ID ${payload.contentId} not found`);
        commit("setActiveSecMenu", {});
        return null;
      }
    } catch (error) {
      console.error("Error fetching screen data:", error);
      commit("setMenuError", error.message);
      throw error;
    } finally {
      commit("setLoadingScreenData", false);
    }
  },

  /**
   * Load primary menus for the program
   */
  async loadPrimaryMenus({ commit, getters }) {
    commit("setLoadingMenus", true);
    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const menusRef = collection(programRef, "primaryMenus");
      const menusQuery = query(menusRef, orderBy("order", "asc"));
      const querySnapshot = await getDocs(menusQuery);

      const menus = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        menus.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setMenusPrimaryMenus", menus);
      return menus;
    } catch (error) {
      console.error("Error loading primary menus:", error);
      commit("setMenuError", error.message);
      commit("setMenusPrimaryMenus", []);
      throw error;
    } finally {
      commit("setLoadingMenus", false);
    }
  },

  /**
   * Get menu by ID
   */
  async getMenuById({ commit, getters }, menuId) {
    if (!menuId) {
      console.error("Menu ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const menuRef = doc(collection(programRef, "primaryMenus"), menuId);
      const menuDoc = await getDoc(menuRef);

      if (menuDoc.exists()) {
        const data = menuDoc.data();
        return {
          id: menuDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Menu with ID ${menuId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting menu by ID:", error);
      commit("setMenuError", error.message);
      return null;
    }
  },

  /**
   * Load current menu
   */
  async loadCurrentMenu({ commit, dispatch }, menuId) {
    if (!menuId) {
      throw new Error("Menu ID is required");
    }

    commit("setLoadingCurrentMenu", true);
    commit("setCurrentMenu", null);
    commit("setMenuError", null);

    try {
      const menu = await dispatch("getMenuById", menuId);
      if (menu) {
        commit("setCurrentMenu", menu);
        return menu;
      } else {
        throw new Error("Menu not found");
      }
    } catch (error) {
      console.error("Error loading current menu:", error);
      commit("setMenuError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentMenu", false);
    }
  },

  /**
   * Create a new menu
   */
  async createMenu({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Menu data with title is required");
    }

    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const menusRef = collection(programRef, "primaryMenus");

      // Check for duplicate titles
      const titleUppercase = payload.title.toUpperCase();
      const titleDupQuery = query(
        menusRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Menu title already exists");
      }

      const menu = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        order: payload.order || 0,
        visible: payload.visible !== undefined ? payload.visible : true,
        items: payload.items || []
      };

      const newMenuRef = await addDoc(menusRef, menu);

      const tempMenu = {
        ...menu,
        id: newMenuRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("addMenusPrimaryMenu", tempMenu);
      dispatch("setSnackbar", "Menu created successfully");
      return tempMenu;
    } catch (error) {
      console.error("Error creating menu:", error);
      commit("setMenuError", error.message);
      throw error;
    }
  },

  /**
   * Update a menu
   */
  async updateMenu({ commit, getters, dispatch }, { id, updates }) {
    if (!id || !updates) {
      throw new Error("Menu ID and updates are required");
    }

    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const menuRef = doc(collection(programRef, "primaryMenus"), id);

      // Verify menu exists
      const menuDoc = await getDoc(menuRef);
      if (!menuDoc.exists()) {
        throw new Error("Menu not found");
      }

      // Check for duplicate titles if title is being updated
      if (updates.title) {
        const titleUppercase = updates.title.toUpperCase();
        const menusRef = collection(programRef, "primaryMenus");
        const titleDupQuery = query(
          menusRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const titleDupSnapshot = await getDocs(titleDupQuery);

        if (!titleDupSnapshot.empty) {
          let duplicated = false;
          titleDupSnapshot.forEach(docSnapshot => {
            if (docSnapshot.id !== id) {
              duplicated = true;
            }
          });
          if (duplicated) {
            throw new Error("Menu title already exists");
          }
        }
        updates.titleUppercase = titleUppercase;
      }

      const updateData = {
        ...updates,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      await updateDoc(menuRef, updateData);

      const updatedMenu = {
        ...menuDoc.data(),
        ...updates,
        id,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      commit("updateMenusPrimaryMenu", updatedMenu);
      dispatch("setSnackbar", "Menu updated successfully");
      return updatedMenu;
    } catch (error) {
      console.error("Error updating menu:", error);
      commit("setMenuError", error.message);
      throw error;
    }
  },

  /**
   * Delete a menu
   */
  async deleteMenu({ commit, getters, dispatch }, menuId) {
    if (!menuId) {
      throw new Error("Menu ID is required");
    }

    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const menuRef = doc(collection(programRef, "primaryMenus"), menuId);

      await deleteDoc(menuRef);

      commit("removeMenusPrimaryMenu", menuId);
      dispatch("setSnackbar", "Menu deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting menu:", error);
      commit("setMenuError", error.message);
      throw error;
    }
  },

  /**
   * Reorder menus
   */
  async reorderMenus({ commit, getters, dispatch }, menuOrderUpdates) {
    if (!menuOrderUpdates || !Array.isArray(menuOrderUpdates)) {
      throw new Error("Menu order updates array is required");
    }

    commit("setMenuError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const batch = [];

      for (const { id, order } of menuOrderUpdates) {
        const menuRef = doc(collection(programRef, "primaryMenus"), id);
        batch.push(
          updateDoc(menuRef, {
            order,
            updated: serverTimestamp(),
            updatedBy: getters.user?.id || "system"
          })
        );
      }

      await Promise.all(batch);

      // Update local state
      menuOrderUpdates.forEach(({ id, order }) => {
        commit("updateMenuOrder", { id, order });
      });

      dispatch("setSnackbar", "Menu order updated successfully");
      return true;
    } catch (error) {
      console.error("Error reordering menus:", error);
      commit("setMenuError", error.message);
      throw error;
    }
  },

  /**
   * Toggle site drawer
   */
  setSiteDrawer({ commit }, payload) {
    commit("setSiteDrawer", Boolean(payload));
  },

  /**
   * Toggle mobile menu
   */
  setMobileMenuOpen({ commit }, payload) {
    commit("setMobileMenuOpen", Boolean(payload));
  },

  /**
   * Toggle submenu
   */
  toggleSubMenu({ commit, state }, menuId) {
    const currentState = state.subMenuOpen[menuId] || false;
    commit("setSubMenuOpen", { menuId, open: !currentState });
  },

  /**
   * Set submenu state
   */
  setSubMenuOpen({ commit }, { menuId, open }) {
    commit("setSubMenuOpen", { menuId, open: Boolean(open) });
  },

  /**
   * Close all submenus
   */
  closeAllSubMenus({ commit }) {
    commit("clearSubMenus");
  },

  /**
   * Clear current menu
   */
  clearCurrentMenu({ commit }) {
    commit("setCurrentMenu", null);
  },

  /**
   * Clear error state
   */
  clearMenuError({ commit }) {
    commit("setMenuError", null);
  }
};

const mutations = {
  setActiveSecMenu(state, payload) {
    state.activeSecMenu = payload;
  },

  setMenusPrimaryMenus(state, payload) {
    state.primaryMenus = payload;
  },

  setCurrentMenu(state, payload) {
    state.currentMenu = payload;
  },

  addMenusPrimaryMenu(state, payload) {
    state.primaryMenus = [...state.primaryMenus, payload].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
  },

  updateMenusPrimaryMenu(state, payload) {
    state.primaryMenus = state.primaryMenus
      .map(menu => {
        if (menu.id === payload.id) {
          return { ...menu, ...payload };
        }
        return menu;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  },

  removeMenusPrimaryMenu(state, menuId) {
    state.primaryMenus = state.primaryMenus.filter(menu => menu.id !== menuId);
  },

  updateMenuOrder(state, { id, order }) {
    state.primaryMenus = state.primaryMenus
      .map(menu => {
        if (menu.id === id) {
          return { ...menu, order };
        }
        return menu;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  },

  setSiteDrawer(state, payload) {
    state.siteDrawer = Boolean(payload);
  },

  setMobileMenuOpen(state, payload) {
    state.mobileMenuOpen = Boolean(payload);
  },

  setSubMenuOpen(state, { menuId, open }) {
    state.subMenuOpen = {
      ...state.subMenuOpen,
      [menuId]: Boolean(open)
    };
  },

  clearSubMenus(state) {
    state.subMenuOpen = {};
  },

  setLoadingMenus(state, payload) {
    state.loadingMenus = Boolean(payload);
  },

  setLoadingCurrentMenu(state, payload) {
    state.loadingCurrentMenu = Boolean(payload);
  },

  setLoadingScreenData(state, payload) {
    state.loadingScreenData = Boolean(payload);
  },

  setMenuError(state, payload) {
    state.error = payload;
  }
};

const getters = {
  /**
   * Get active secondary menu
   */
  activeSecMenu(state) {
    return state.activeSecMenu;
  },

  /**
   * Get all menus primary menus (renamed to avoid conflict)
   */
  menusPrimaryMenus(state) {
    return state.primaryMenus;
  },

  /**
   * Get visible primary menus only
   */
  visibleMenusPrimaryMenus(state) {
    return state.primaryMenus.filter(menu => menu.visible !== false);
  },

  /**
   * Get current menu
   */
  currentMenu(state) {
    return state.currentMenu;
  },

  /**
   * Get menu by ID
   */
  getMenuById: state => id => {
    return state.primaryMenus.find(menu => menu.id === id) || null;
  },

  /**
   * Get menu by title
   */
  getMenuByTitle: state => title => {
    const titleUppercase = title.toUpperCase();
    return (
      state.primaryMenus.find(menu => menu.titleUppercase === titleUppercase) ||
      null
    );
  },

  /**
   * Get site drawer state
   */
  siteDrawer(state) {
    return state.siteDrawer;
  },

  /**
   * Get mobile menu state
   */
  mobileMenuOpen(state) {
    return state.mobileMenuOpen;
  },

  /**
   * Get submenu state for a specific menu
   */
  getSubMenuOpen: state => menuId => {
    return state.subMenuOpen[menuId] || false;
  },

  /**
   * Get all submenu states
   */
  subMenuOpen(state) {
    return state.subMenuOpen;
  },

  /**
   * Get loading states
   */
  loadingMenus(state) {
    return state.loadingMenus;
  },

  loadingCurrentMenu(state) {
    return state.loadingCurrentMenu;
  },

  loadingScreenData(state) {
    return state.loadingScreenData;
  },

  /**
   * Get menu error state (renamed to avoid conflict)
   */
  menuError(state) {
    return state.error;
  },

  /**
   * Check if any menus are loading (renamed to avoid conflict)
   */
  menusIsLoading(state) {
    return (
      state.loadingMenus || state.loadingCurrentMenu || state.loadingScreenData
    );
  },

  /**
   * Get menus count
   */
  menusCount(state) {
    return state.primaryMenus.length;
  },

  /**
   * Get visible menus count
   */
  visibleMenusCount(state) {
    return state.primaryMenus.filter(menu => menu.visible !== false).length;
  },

  /**
   * Check if menus exist
   */
  hasMenus(state) {
    return state.primaryMenus.length > 0;
  },

  /**
   * Check if visible menus exist
   */
  hasVisibleMenus(state) {
    return state.primaryMenus.some(menu => menu.visible !== false);
  },

  /**
   * Get menus as options for dropdowns
   */
  menuOptions(state) {
    return state.primaryMenus.map(menu => ({
      text: menu.title,
      value: menu.id
    }));
  },

  /**
   * Get visible menus as options for dropdowns
   */
  visibleMenuOptions(state) {
    return state.primaryMenus
      .filter(menu => menu.visible !== false)
      .map(menu => ({
        text: menu.title,
        value: menu.id
      }));
  },

  /**
   * Check if any submenu is open
   */
  hasOpenSubMenus(state) {
    return Object.values(state.subMenuOpen).some(open => open);
  },

  /**
   * Get next menu order number
   */
  nextMenuOrder(state) {
    if (state.primaryMenus.length === 0) return 0;
    return Math.max(...state.primaryMenus.map(menu => menu.order || 0)) + 1;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
